import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { getWalletTransaction } from "../apis/wallet";
import { fmtINR } from "../utils/money";
import { fmtIST } from "../utils/datetime";
import {
  CATEGORY_LABELS,
  isCredit,
  parseMeta,
  txnTitle,
} from "../utils/walletTxn";

const WalletTransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getWalletTransaction(id);
        if (alive && res.data?.success) setTxn(res.data.data);
      } catch (err) {
        toast.error(err.response?.data?.error || "Transaction not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="wallet-empty" style={{ minHeight: "40vh" }}>
        <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
      </div>
    );
  }

  if (!txn) {
    return (
      <div className="wallet-page">
        <p>Transaction not found.</p>
        <Link to="/wallet">Back to wallet</Link>
      </div>
    );
  }

  const meta = parseMeta(txn.meta);
  const r = txn.resolved || {};
  const credit = isCredit(txn.type, meta, txn.sign);

  return (
    <div className="wallet-page wspend">
      <button type="button" className="wspend-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={15} />
        Back
      </button>
      <div className="page-head">
        <div>
          <h2>{txnTitle(txn.type, meta, r)}</h2>
          <p>{fmtIST(txn.created_at, { year: "numeric", month: "short", day: "2-digit" })}</p>
        </div>
        <div className="grow" />
        <span className={credit ? "wallet-amt-cr wtxn-hero" : "wallet-amt-db wtxn-hero"}>
          {credit ? "+" : "−"} {fmtINR(txn.amount)}
        </span>
      </div>

      <div className="wtxn-grid">
        <div className="wallet-card">
          <div className="wallet-card-pad">
            <h3>Charge</h3>
          </div>
          <div className="wtxn-kvs">
            <div className="ping-kv"><span className="k">Type</span><span className="v">{txn.type}</span></div>
            <div className="ping-kv"><span className="k">Amount</span><span className="v">{fmtINR(txn.amount)}</span></div>
            <div className="ping-kv"><span className="k">Balance after</span><span className="v">{fmtINR(txn.balance_after)}</span></div>
            <div className="ping-kv"><span className="k">When</span><span className="v">{fmtIST(txn.created_at, { year: "numeric" })}</span></div>
            <div className="ping-kv"><span className="k">Reference</span><span className="v wtxn-mono">{txn.reference_id || "—"}</span></div>
          </div>
        </div>

        <div className="wallet-card">
          <div className="wallet-card-pad">
            <h3>Where it went</h3>
          </div>
          <div className="wtxn-kvs">
            <div className="ping-kv">
              <span className="k">Campaign</span>
              <span className="v">
                {r.campaign_id ? (
                  <Link to={`/wallet/spend/${r.campaign_id}`}>{r.campaign_name || `Campaign ${r.campaign_id}`}</Link>
                ) : (r.campaign_name || "—")}
              </span>
            </div>
            <div className="ping-kv"><span className="k">Category</span><span className="v">{CATEGORY_LABELS[r.category] || r.category || "—"}</span></div>
            <div className="ping-kv"><span className="k">Template</span><span className="v">{r.template || "—"}</span></div>
            <div className="ping-kv"><span className="k">Phone</span><span className="v">{r.to || "—"}</span></div>
            <div className="ping-kv"><span className="k">Country</span><span className="v">{r.country || "—"}</span></div>
            <div className="ping-kv"><span className="k">Source</span><span className="v">{r.source || txn.reference_type || "—"}</span></div>
          </div>
        </div>
      </div>

      {txn.linked_refund && (
        <div className="ping-banner ok" style={{ marginTop: 14 }}>
          <div>
            <b>Refunded {fmtINR(txn.linked_refund.amount)}</b>
            <p>
              Failed message credited back {fmtIST(txn.linked_refund.created_at)}.{" "}
              <Link to={`/wallet/transactions/${txn.linked_refund.id}`}>Open refund</Link>
            </p>
          </div>
        </div>
      )}

      {txn.linked_debit && (
        <div className="ping-banner blue" style={{ marginTop: 14 }}>
          <div>
            <b>Original charge {fmtINR(txn.linked_debit.amount)}</b>
            <p>
              Debited {fmtIST(txn.linked_debit.created_at)}.{" "}
              <Link to={`/wallet/transactions/${txn.linked_debit.id}`}>Open charge</Link>
            </p>
          </div>
        </div>
      )}

      {r.campaign_id && (
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Link to={`/wallet/spend/${r.campaign_id}`} className="ping-btn ping-btn-ghost ping-btn-sm">
            Campaign spend
          </Link>
          <Link to={`/campaigns/${r.campaign_id}/analytics`} className="ping-btn ping-btn-ghost ping-btn-sm">
            Delivery analytics
          </Link>
        </div>
      )}
    </div>
  );
};

export default WalletTransactionDetail;
